import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Music, User, MessageCircle, UserPlus, Check, X, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';

interface UserData {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  cover_image: string | null;
}

interface FriendshipStatus {
  status: 'pending' | 'accepted' | 'rejected' | null;
  sender: boolean;
}

function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useUser();
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingChat, setStartingChat] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState<FriendshipStatus | null>(null);
  const [processingFriendRequest, setProcessingFriendRequest] = useState(false);
  
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) throw error;
        setUser(data);

        if (currentUser) {
          const { data: friendshipData } = await supabase
            .rpc('get_friendship_status', {
              user1_id: currentUser.id,
              user2_id: data.id
            });

          setFriendshipStatus(friendshipData?.[0] || null);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUser();
    }
  }, [userId, currentUser]);

  const handleStartChat = async () => {
    if (!user || !currentUser || startingChat) return;

    setStartingChat(true);
    try {
      // Determine user order to match the CHECK constraint (user1_id < user2_id)
      const [user1Id, user2Id] = [currentUser.id, user.id].sort();
      
      // Check for existing chat
      const { data: existingChats, error: chatError } = await supabase
        .from('chats')
        .select('id')
        .eq('user1_id', user1Id)
        .eq('user2_id', user2Id)
        .limit(1);

      if (chatError) throw chatError;

      let chatId;
      if (existingChats && existingChats.length > 0) {
        chatId = existingChats[0].id;
      } else {
        // Create new chat with sorted user IDs
        const { data: newChat, error: createError } = await supabase
          .from('chats')
          .insert({
            user1_id: user1Id,
            user2_id: user2Id,
            created_at: new Date().toISOString()
          })
          .select('id')
          .single();

        if (createError) throw createError;
        chatId = newChat.id;
      }

      if (!chatId) {
        throw new Error('Failed to get chat ID');
      }

      // Navigate to chat
      navigate(`/chat/${chatId}`);
    } catch (error) {
      console.error('Error starting chat:', error);
      alert('Failed to start chat. Please try again.');
    } finally {
      setStartingChat(false);
    }
  };

  const handleFriendRequest = async () => {
    if (!user || !currentUser || processingFriendRequest) return;

    setProcessingFriendRequest(true);
    try {
      const { error } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: currentUser.id,
          receiver_id: user.id,
          status: 'pending'
        });

      if (error) throw error;

      // Create notification for the receiver
      await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          type: 'friend_request',
          actor_id: currentUser.id
        });

      setFriendshipStatus({ status: 'pending', sender: true });
    } catch (error) {
      console.error('Error sending friend request:', error);
      alert('Failed to send friend request. Please try again.');
    } finally {
      setProcessingFriendRequest(false);
    }
  };

  const handleFriendRequestResponse = async (accept: boolean) => {
    if (!user || !currentUser || processingFriendRequest) return;

    setProcessingFriendRequest(true);
    try {
      const { error } = await supabase
        .from('friend_requests')
        .update({
          status: accept ? 'accepted' : 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('sender_id', user.id)
        .eq('receiver_id', currentUser.id);

      if (error) throw error;

      if (accept) {
        // Create notification for the sender
        await supabase
          .from('notifications')
          .insert({
            user_id: user.id,
            type: 'friend_accepted',
            actor_id: currentUser.id
          });
      }

      setFriendshipStatus({ status: accept ? 'accepted' : 'rejected', sender: false });
    } catch (error) {
      console.error('Error responding to friend request:', error);
      alert('Failed to respond to friend request. Please try again.');
    } finally {
      setProcessingFriendRequest(false);
    }
  };

  const renderFriendshipButton = () => {
    if (!currentUser || currentUser.id === user?.id) return null;

    if (!friendshipStatus) {
      return (
        <button
          onClick={handleFriendRequest}
          disabled={processingFriendRequest}
          className={`flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors ${
            processingFriendRequest ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <UserPlus className="h-5 w-5" />
          <span>Add Friend</span>
        </button>
      );
    }

    switch (friendshipStatus.status) {
      case 'pending':
        return friendshipStatus.sender ? (
          <button
            disabled
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-full"
          >
            <Clock className="h-5 w-5" />
            <span>Request Sent</span>
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => handleFriendRequestResponse(true)}
              disabled={processingFriendRequest}
              className={`flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors ${
                processingFriendRequest ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Check className="h-5 w-5" />
              <span>Accept</span>
            </button>
            <button
              onClick={() => handleFriendRequestResponse(false)}
              disabled={processingFriendRequest}
              className={`flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors ${
                processingFriendRequest ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <X className="h-5 w-5" />
              <span>Decline</span>
            </button>
          </div>
        );
      case 'accepted':
        return (
          <div className="flex items-center gap-4">
            <span className="text-green-600 flex items-center gap-2">
              <Check className="h-5 w-5" />
              Friends
            </span>
            <button
              onClick={handleStartChat}
              disabled={startingChat}
              className={`flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors ${
                startingChat ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <MessageCircle className="h-5 w-5" />
              <span>Message</span>
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-2xl mx-auto pt-4 px-4">
          <Link
            to="/"
            className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Link>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-2xl mx-auto pt-4 px-4">
          <Link
            to="/"
            className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Link>
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-red-600">{error || 'User not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-2xl mx-auto space-y-6 pt-4 px-4">
        <Link
          to="/"
          className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="relative h-48 bg-gradient-to-r from-indigo-500 to-purple-500">
            {user.cover_image && (
              <img
                src={user.cover_image}
                alt="Cover"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute -bottom-16 left-8">
              <div className="w-32 h-32 rounded-full border-4 border-white bg-gray-200 overflow-hidden">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-12 w-12 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="pt-20 pb-6 px-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {user.display_name || user.username}
                </h1>
                <p className="text-gray-600">@{user.username}</p>
                {user.bio && (
                  <p className="text-gray-600 mt-2">{user.bio}</p>
                )}
              </div>
              {renderFriendshipButton()}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Music className="h-6 w-6 text-indigo-600 mr-3" />
              <div>
                <h3 className="font-medium">Music Match</h3>
                <p className="text-sm text-gray-600">Coming soon</p>
              </div>
            </div>
          </div>
        </div>

        <Link
          to="/compare"
          className="block w-full py-3 px-4 bg-indigo-600 text-white text-center rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Compare Music Taste
        </Link>
      </div>
    </div>
  );
}

export default UserProfile;